<template>
  <v-container fluid class="pa-0 institution-ebay-page">
    <div
      class="institution-ebay-toolbar bg-red-darken-3 text-white px-3 py-2 py-md-3 px-md-6 px-lg-8"
    >
      <div class="text-subtitle-1 institution-ebay-toolbar-title">
        {{ $t("ebay_exclusions.label") }}
      </div>
    </div>

    <div class="col-12 institution-ebay-content">
      <v-card flat class="pa-4">
        <v-alert v-if="pageError" type="error" variant="tonal" class="mb-4">
          {{ pageError }}
        </v-alert>

        <v-row align="center" class="mb-2">
          <v-col
            v-if="isSuperAdmin"
            cols="12"
            md="4"
          >
            <v-autocomplete
              v-model="filterInstitutionId"
              :items="institutions"
              :loading="loadingInstitutions"
              :label="$t('ebay_exclusions.institution')"
              item-title="title"
              item-value="_id"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="showInactive"
              :label="$t('ebay_exclusions.show_inactive')"
              color="red-darken-3"
              hide-details
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-md-end">
            <v-btn color="red-darken-3" @click="openAdd">
              {{ $t("ebay_exclusions.add") }}
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="displayedRows"
          :loading="loading"
          :items-per-page="25"
          class="elevation-1"
        >
          <template #item.scope="{ item }">
            <v-chip size="small" :color="item.scope === 'global' ? 'red' : 'grey'">
              {{ scopeLabel(item.scope) }}
            </v-chip>
          </template>
          <template #item.institution_id="{ item }">
            {{ institutionName(item.institution_id) }}
          </template>
          <template #item.active="{ item }">
            {{
              item.active
                ? $t("ebay_exclusions.active")
                : $t("ebay_exclusions.inactive")
            }}
          </template>
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template #item.actions="{ item }">
            <v-tooltip v-if="item.readOnly" location="top">
              <template #activator="{ props }">
                <v-icon v-bind="props" size="small">mdi-lock</v-icon>
              </template>
              <span>{{ $t("ebay_exclusions.read_only") }}</span>
            </v-tooltip>
            <template v-else>
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-pencil"
                    variant="text"
                    size="small"
                    :aria-label="$t('ebay_exclusions.tooltip_edit')"
                    @click="openEdit(item)"
                  />
                </template>
                <span>{{ $t("ebay_exclusions.tooltip_edit") }}</span>
              </v-tooltip>
              <v-tooltip v-if="item.active" location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-stop-circle"
                    variant="text"
                    size="small"
                    :aria-label="$t('ebay_exclusions.tooltip_end_listings')"
                    @click="openEndPreview(item)"
                  />
                </template>
                <span>{{ $t("ebay_exclusions.tooltip_end_listings") }}</span>
              </v-tooltip>
              <v-tooltip v-if="item.active" location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-cancel"
                    variant="text"
                    size="small"
                    :aria-label="$t('ebay_exclusions.tooltip_deactivate')"
                    @click="confirmDeactivate(item)"
                  />
                </template>
                <span>{{ $t("ebay_exclusions.tooltip_deactivate") }}</span>
              </v-tooltip>
              <v-tooltip v-else location="top">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-restore"
                    variant="text"
                    size="small"
                    :aria-label="$t('ebay_exclusions.tooltip_reactivate')"
                    @click="reactivate(item)"
                  />
                </template>
                <span>{{ $t("ebay_exclusions.tooltip_reactivate") }}</span>
              </v-tooltip>
              <v-chip
                v-if="runningJobs[item._id]"
                size="small"
                color="orange"
                class="ml-1"
                @click="reopenJob(item)"
              >
                {{ $t("ebay_exclusions.ending_in_progress") }}
              </v-chip>
            </template>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="formDialog" max-width="640">
      <v-card>
        <v-card-title>
          {{
            formMode === "add"
              ? $t("ebay_exclusions.add")
              : $t("ebay_exclusions.edit")
          }}
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ formError }}
          </v-alert>
          <v-autocomplete
            v-if="formMode === 'add'"
            v-model="form.selectedPart"
            v-model:search="searchTitle"
            :items="partSuggestions"
            :loading="loadingTitle"
            :label="$t('ebay_exclusions.search_title')"
            item-title="label"
            return-object
            variant="outlined"
            density="compact"
            class="mb-3"
            clearable
            hide-no-data
            autocomplete="off"
          />
          <v-text-field
            v-model="form.partCode"
            :label="$t('ebay_exclusions.part_code')"
            disabled
            variant="outlined"
            density="compact"
            class="mb-3"
            hide-spin-buttons
          />
          <v-text-field
            v-if="formMode === 'edit'"
            v-model="form.titleIt"
            :label="$t('ebay_exclusions.title_it')"
            disabled
            variant="outlined"
            density="compact"
            class="mb-3"
          />
          <v-textarea
            v-model="form.reason"
            :label="$t('ebay_exclusions.reason')"
            maxlength="200"
            rows="2"
            variant="outlined"
            density="compact"
            class="mb-3"
          />
          <template v-if="formMode === 'add' && isSuperAdmin">
            <v-radio-group v-model="form.scope" class="mb-2" hide-details>
              <v-radio
                :label="$t('ebay_exclusions.scope_global')"
                value="global"
              />
              <v-radio
                :label="$t('ebay_exclusions.scope_institution')"
                value="institution"
              />
            </v-radio-group>
            <v-autocomplete
              v-if="form.scope === 'institution'"
              v-model="form.institution_id"
              :items="institutions"
              :label="$t('ebay_exclusions.institution')"
              item-title="title"
              item-value="_id"
              variant="outlined"
              density="compact"
            />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="formDialog = false">
            {{ $t("ebay_exclusions.cancel") }}
          </v-btn>
          <v-btn
            color="red-darken-3"
            :loading="saving"
            @click="submitForm"
          >
            {{ $t("ebay_exclusions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deactivateDialog" max-width="480">
      <v-card>
        <v-card-title>{{ $t("ebay_exclusions.deactivate") }}</v-card-title>
        <v-card-text>
          {{ $t("ebay_exclusions.confirm_deactivate") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deactivateDialog = false">
            {{ $t("ebay_exclusions.cancel") }}
          </v-btn>
          <v-btn
            color="red-darken-3"
            :loading="saving"
            @click="deactivate"
          >
            {{ $t("ebay_exclusions.deactivate") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="livePartsDialog" max-width="520">
      <v-card>
        <v-card-title>
          {{ $t("ebay_exclusions.live_parts_title") }}
        </v-card-title>
        <v-card-text>
          {{
            $t("ebay_exclusions.live_parts_body", {
              n: livePartsCount,
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red-darken-3" @click="endFromLiveParts">
            {{ $t("ebay_exclusions.end_listings") }}
          </v-btn>
          <v-btn variant="text" @click="livePartsDialog = false">
            {{ $t("ebay_exclusions.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="endDialog" max-width="720" @update:model-value="onEndDialogToggle">
      <v-card>
        <v-card-title>{{ endDialogTitle }}</v-card-title>
        <v-card-text>
          <template v-if="endView === 'preview' && preview">
            <v-alert v-if="previewNotice" type="warning" variant="tonal" class="mb-3">
              {{ previewNotice }}
            </v-alert>
            <div class="mb-2">
              {{ $t("ebay_exclusions.parts_involved", { n: preview.partsCount }) }}
            </div>
            <div class="mb-3">
              {{ $t("ebay_exclusions.listings_to_end", { n: listingsToEnd }) }}
            </div>
            <div class="d-flex flex-wrap ga-2 mb-3">
              <v-chip v-for="site in perSiteEntries" :key="site.key" size="small">
                {{ site.key }}: {{ site.count }}
              </v-chip>
            </div>
            <v-table v-if="endScope === 'global' && preview.perInstitution" density="compact" class="mb-3">
              <thead>
                <tr>
                  <th>{{ $t("ebay_exclusions.institution") }}</th>
                  <th>{{ $t("ebay_exclusions.parts_short") }}</th>
                  <th>{{ $t("ebay_exclusions.listings_short") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="yard in preview.perInstitution" :key="yard.institution_id">
                  <td>{{ yard.title }}</td>
                  <td>{{ yard.parts }}</td>
                  <td>{{ yard.listings }}</td>
                </tr>
              </tbody>
            </v-table>
            <v-expansion-panels v-if="preview.sample && preview.sample.length" class="mb-3">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  {{ $t("ebay_exclusions.sample") }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div v-for="row in preview.sample" :key="row.part_id" class="text-body-2 mb-1">
                    {{ sampleQr(row) }} — {{ row.title }}
                    ({{ (row.sites || []).join(", ") }})
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-alert type="warning" variant="tonal">
              {{ $t("ebay_exclusions.end_warning") }}
            </v-alert>
          </template>

          <template v-else-if="endView === 'progress' && job">
            <v-progress-linear
              :model-value="jobProgressPercent"
              color="red-darken-3"
              height="8"
              class="mb-3"
            />
            <div>
              {{
                $t("ebay_exclusions.listings_progress", {
                  done: (job.progress && job.progress.listingsEnded) || 0,
                  total: (job.totals && job.totals.listings) || 0,
                })
              }}
            </div>
          </template>

          <template v-else-if="endView === 'errors' && job">
            <v-alert type="warning" variant="tonal" class="mb-3">
              {{ $t("ebay_exclusions.completed_with_errors") }}
            </v-alert>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t("ebay_exclusions.part_id") }}</th>
                  <th>{{ $t("ebay_exclusions.site") }}</th>
                  <th>{{ $t("ebay_exclusions.error") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(err, index) in job.errors" :key="index">
                  <td>{{ err.part_id }}</td>
                  <td>{{ err.site }}</td>
                  <td>{{ err.errorMsg }}</td>
                </tr>
              </tbody>
            </v-table>
            <div v-if="job.errorOverflowCount > 0" class="mt-2">
              {{ $t("ebay_exclusions.error_overflow", { n: job.errorOverflowCount }) }}
            </div>
          </template>

          <template v-else-if="endView === 'failed' && job">
            <v-alert type="error" variant="tonal">
              {{ (job.errors[0] && job.errors[0].errorMsg) || $t("ebay_exclusions.job_failed") }}
              {{ $t("ebay_exclusions.rerun_safe") }}
            </v-alert>
          </template>

          <template v-else-if="endView === 'poll_error'">
            <v-alert type="error" variant="tonal" class="mb-3">
              {{ $t("ebay_exclusions.poll_failed") }}
            </v-alert>
            <v-btn color="red-darken-3" @click="retryPoll">
              {{ $t("ebay_exclusions.retry") }}
            </v-btn>
          </template>

          <v-alert v-else-if="endView === 'job_running_notice'" type="info" variant="tonal">
            {{ $t("ebay_exclusions.job_already_running") }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="endDialog = false">
            {{ endView === "preview" ? $t("ebay_exclusions.cancel") : $t("ebay_exclusions.close") }}
          </v-btn>
          <v-btn
            v-if="endView === 'preview'"
            color="red-darken-3"
            :loading="confirmingEnd"
            @click="confirmEndListings"
          >
            {{ $t("ebay_exclusions.confirm_end") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.value" :color="snackbar.color" location="top">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import getRequest from "@/api/getRequest";
import postRequest from "@/api/postRequest";
import putRequest from "@/api/putRequest";
import deleteRequest from "@/api/deleteRequest";

const TITLE_DEBOUNCE_MS = 300;
const POLL_MS = 10000;
const POLL_FAIL_LIMIT = 3;
const TERMINAL_JOB = ["completed", "completed_with_errors", "failed"];

export default {
  name: "EbayExclusions",
  data() {
    return {
      rows: [],
      institutions: [],
      filterInstitutionId: null,
      showInactive: false,
      loading: false,
      loadingInstitutions: false,
      loadingTitle: false,
      saving: false,
      pageError: "",
      formDialog: false,
      formMode: "add",
      formError: "",
      searchTitle: "",
      partSuggestions: [],
      selectingItem: false,
      titleTimer: null,
      deactivateDialog: false,
      rowToDeactivate: null,
      livePartsDialog: false,
      livePartsCount: 0,
      createdExclusionId: null,
      createdScope: null,
      endDialog: false,
      endView: "preview",
      endExclusionId: null,
      endScope: null,
      preview: null,
      previewNotice: "",
      job: null,
      confirmingEnd: false,
      runningJobs: {},
      pollTimer: null,
      pollFailures: {},
      stalledJobs: {},
      snackbar: { value: false, message: "", color: "info" },
      form: {
        _id: null,
        selectedPart: null,
        partCode: "",
        titleIt: "",
        reason: "",
        scope: "institution",
        institution_id: null,
      },
    };
  },
  computed: {
    isSuperAdmin() {
      return this.$store.getters.role === "SuperAdmin";
    },
    headers() {
      return [
        { title: this.$t("ebay_exclusions.part_code"), key: "partCode" },
        { title: this.$t("ebay_exclusions.title_it"), key: "titleIt" },
        { title: this.$t("ebay_exclusions.scope"), key: "scope" },
        { title: this.$t("ebay_exclusions.institution"), key: "institution_id" },
        { title: this.$t("ebay_exclusions.reason"), key: "reason" },
        { title: this.$t("ebay_exclusions.active"), key: "active" },
        { title: this.$t("ebay_exclusions.created_at"), key: "createdAt" },
        { title: this.$t("ebay_exclusions.actions"), key: "actions", sortable: false },
      ];
    },
    displayedRows() {
      if (this.showInactive) return this.rows;
      return this.rows.filter((row) => row.active);
    },
    listingsToEnd() {
      if (!this.preview) return 0;
      return (this.preview.listingsCount || 0) + (this.preview.legacyParts || 0);
    },
    perSiteEntries() {
      const sites = (this.preview && this.preview.perSite) || {};
      return Object.keys(sites).map((key) => ({ key, count: sites[key] }));
    },
    jobProgressPercent() {
      const total = this.job && this.job.totals && this.job.totals.parts;
      const done = this.job && this.job.progress && this.job.progress.partsDone;
      if (!total) return 0;
      return Math.round((done / total) * 100);
    },
    endDialogTitle() {
      if (this.endView === "preview") return this.$t("ebay_exclusions.end_dialog_title");
      if (this.endView === "errors") return this.$t("ebay_exclusions.completed_with_errors");
      if (this.endView === "failed") return this.$t("ebay_exclusions.job_failed");
      return this.$t("ebay_exclusions.ending_in_progress");
    },
  },
  watch: {
    filterInstitutionId() {
      this.fetchRows();
    },
    searchTitle(newValue) {
      if (this.selectingItem) {
        this.selectingItem = false;
        return;
      }
      clearTimeout(this.titleTimer);
      if (!newValue || String(newValue).trim().length < 2) {
        this.partSuggestions = [];
        return;
      }
      this.titleTimer = setTimeout(() => {
        this.searchParts(newValue);
      }, TITLE_DEBOUNCE_MS);
    },
    "form.selectedPart"(part) {
      if (!part || typeof part !== "object") {
        this.form.partCode = "";
        this.form.titleIt = "";
        return;
      }
      this.selectingItem = true;
      this.form.partCode = part.partCode;
      this.form.titleIt = (part.title && part.title.it) || "";
    },
  },
  created() {
    this.fetchInstitutions();
    this.fetchRows();
  },
  beforeUnmount() {
    clearTimeout(this.titleTimer);
    this.stopPolling();
  },
  methods: {
    emptyForm() {
      return {
        _id: null,
        selectedPart: null,
        partCode: "",
        titleIt: "",
        reason: "",
        scope: this.$store.getters.role === "SuperAdmin" ? "global" : "institution",
        institution_id: null,
      };
    },
    handleAuthError(err) {
      const status = err && err.response && err.response.status;
      if (status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        this.$store.dispatch("logoutAction");
        this.$store.dispatch("finishLoading");
        this.$router.push({ path: "/auth" });
        return true;
      }
      return false;
    },
    apiErrorMessage(err, fallbackKey) {
      const status = err && err.response && err.response.status;
      if (status === 403) return this.$t("ebay_exclusions.forbidden");
      if (status === 409) return this.$t("ebay_exclusions.duplicate");
      const msg = err && err.response && err.response.data && err.response.data.errorMsg;
      return msg || this.$t(fallbackKey);
    },
    fetchInstitutions() {
      this.loadingInstitutions = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = (response && response.institutions) || [];
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.institutions = [];
        })
        .finally(() => {
          this.loadingInstitutions = false;
        });
    },
    fetchRows() {
      this.loading = true;
      this.pageError = "";
      let query = "";
      if (this.isSuperAdmin && this.filterInstitutionId) {
        query = `institutionId=${this.filterInstitutionId}`;
      }
      getRequest("/ebay/exclusions", query)
        .then((response) => {
          this.rows = (response && response.rows) || [];
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.rows = [];
          this.pageError = this.apiErrorMessage(err, "ebay_exclusions.load_error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchParts(title) {
      this.loadingTitle = true;
      const language = this.$store.getters.language || "it";
      getRequest(
        "/info-car/parts",
        `title=${encodeURIComponent(title)}&language=${language}`,
      )
        .then((response) => {
          const parts = (response && response.parts) || [];
          this.partSuggestions = parts.map((part) => ({
            ...part,
            label:
              (part.title && (part.title[language] || part.title.it)) ||
              String(part.partCode),
          }));
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.partSuggestions = [];
        })
        .finally(() => {
          this.loadingTitle = false;
        });
    },
    parsePartCode(value) {
      const n = typeof value === "number" ? value : Number(value);
      if (!Number.isInteger(n) || n <= 0) return null;
      return n;
    },
    openAdd() {
      this.formMode = "add";
      this.formError = "";
      this.form = this.emptyForm();
      this.searchTitle = "";
      this.partSuggestions = [];
      this.formDialog = true;
    },
    openEdit(item) {
      this.formMode = "edit";
      this.formError = "";
      this.form = {
        _id: item._id,
        selectedPart: null,
        partCode: item.partCode,
        titleIt: item.titleIt || "",
        reason: item.reason || "",
        scope: item.scope,
        institution_id: item.institution_id,
      };
      this.formDialog = true;
    },
    submitForm() {
      if (this.formMode === "add") this.createExclusion();
      else this.updateExclusion();
    },
    createExclusion() {
      const partCode = this.parsePartCode(this.form.partCode);
      if (partCode == null) {
        this.formError = this.$t("ebay_exclusions.required_part_code");
        return;
      }
      const scope = this.isSuperAdmin ? this.form.scope : "institution";
      if (
        this.isSuperAdmin &&
        scope === "institution" &&
        !this.form.institution_id
      ) {
        this.formError = this.$t("ebay_exclusions.required_institution");
        return;
      }
      const payload = { partCode, scope };
      if (this.form.titleIt && this.form.titleIt.trim()) {
        payload.titleIt = this.form.titleIt.trim();
      }
      if (this.form.reason && this.form.reason.trim()) {
        payload.reason = this.form.reason.trim();
      }
      if (this.isSuperAdmin && scope === "institution") {
        payload.institution_id = this.form.institution_id;
      }
      this.saving = true;
      this.formError = "";
      postRequest
        .object("/ebay/exclusions", payload)
        .then((res) => {
          this.formDialog = false;
          this.fetchRows();
          const count = res && res.data && res.data.livePartsCount;
          this.createdExclusionId = res && res.data && res.data._id;
          this.createdScope = res && res.data && res.data.scope;
          if (count > 0) {
            this.livePartsCount = count;
            this.livePartsDialog = true;
          }
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.formError = this.apiErrorMessage(err, "ebay_exclusions.save_error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    updateExclusion() {
      const payload = {
        reason: this.form.reason,
      };
      this.saving = true;
      this.formError = "";
      putRequest(`/ebay/exclusions/${this.form._id}`, "", payload)
        .then(() => {
          this.formDialog = false;
          this.fetchRows();
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.formError = this.apiErrorMessage(err, "ebay_exclusions.save_error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDeactivate(item) {
      this.rowToDeactivate = item;
      this.deactivateDialog = true;
    },
    deactivate() {
      if (!this.rowToDeactivate) return;
      this.saving = true;
      deleteRequest(`/ebay/exclusions/${this.rowToDeactivate._id}`, "")
        .then(() => {
          this.deactivateDialog = false;
          this.rowToDeactivate = null;
          this.fetchRows();
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.pageError = this.apiErrorMessage(err, "ebay_exclusions.save_error");
          this.deactivateDialog = false;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    reactivate(item) {
      this.saving = true;
      putRequest(`/ebay/exclusions/${item._id}`, "", { active: true })
        .then(() => {
          this.fetchRows();
        })
        .catch((err) => {
          if (this.handleAuthError(err)) return;
          this.pageError = this.apiErrorMessage(err, "ebay_exclusions.save_error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    scopeLabel(scope) {
      return scope === "global"
        ? this.$t("ebay_exclusions.scope_global")
        : this.$t("ebay_exclusions.scope_institution");
    },
    institutionName(id) {
      if (!id) return "—";
      const found = this.institutions.find((inst) => String(inst._id) === String(id));
      return found ? found.title : String(id);
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString();
    },
    sampleQr(row) {
      if (!row) return "";
      return String(row.id_string || row.part_id || "").slice(-6);
    },
    showSnack(message, color) {
      this.snackbar = { value: true, message, color: color || "info" };
    },
    statusOf(err) {
      return err && err.response && err.response.status;
    },
    handleEndHttpError(err) {
      if (this.handleAuthError(err)) return true;
      const status = this.statusOf(err);
      if (status === 403) {
        this.showSnack(this.$t("ebay_exclusions.forbidden"), "error");
        return true;
      }
      if (status === 404) {
        this.showSnack(this.$t("ebay_exclusions.not_found"), "error");
        this.fetchRows();
        return true;
      }
      return false;
    },
    openEndPreview(item) {
      this.endExclusionId = item._id;
      this.endScope = item.scope;
      this.previewNotice = "";
      this.confirmingEnd = true;
      postRequest
        .object(`/ebay/exclusions/${item._id}/end-listings/preview`, {})
        .then((res) => {
          const data = res.data || {};
          if (!data.partsCount) {
            this.showSnack(this.$t("ebay_exclusions.no_live_listings"), "info");
            return;
          }
          this.preview = data;
          this.endView = "preview";
          this.endDialog = true;
        })
        .catch((err) => {
          if (this.handleEndHttpError(err)) return;
          this.showSnack(this.$t("ebay_exclusions.preview_error"), "error");
        })
        .finally(() => {
          this.confirmingEnd = false;
        });
    },
    endFromLiveParts() {
      this.livePartsDialog = false;
      if (!this.createdExclusionId) return;
      this.openEndPreview({
        _id: this.createdExclusionId,
        scope: this.createdScope,
      });
    },
    confirmEndListings() {
      if (!this.endExclusionId || !this.preview) return;
      this.confirmingEnd = true;
      this.previewNotice = "";
      postRequest
        .object(`/ebay/exclusions/${this.endExclusionId}/end-listings`, {
          confirmListingsCount: this.listingsToEnd,
        })
        .then((res) => {
          const jobId = res.data && res.data.jobId;
          const totals = (res.data && res.data.totals) || {
            parts: this.preview.partsCount,
            listings: this.listingsToEnd,
          };
          this.runningJobs = { ...this.runningJobs, [this.endExclusionId]: jobId };
          this.job = {
            status: "pending",
            totals,
            progress: { partsDone: 0, listingsEnded: 0 },
            errors: [],
            errorOverflowCount: 0,
          };
          this.endView = "progress";
          this.ensurePolling();
        })
        .catch((err) => {
          if (this.handleEndHttpError(err)) return;
          if (this.statusOf(err) === 409) {
            this.applyExecuteConflict(err.response.data || {});
            return;
          }
          this.showSnack(this.$t("ebay_exclusions.preview_error"), "error");
        })
        .finally(() => {
          this.confirmingEnd = false;
        });
    },
    applyExecuteConflict(data) {
      if (data.jobId) {
        this.runningJobs = { ...this.runningJobs, [this.endExclusionId]: data.jobId };
        this.job = this.job || {
          status: "running",
          totals: { parts: 0, listings: 0 },
          progress: { partsDone: 0, listingsEnded: 0 },
          errors: [],
          errorOverflowCount: 0,
        };
        this.endView = "progress";
        this.endDialog = true;
        this.ensurePolling();
        return;
      }
      if (data.partsCount != null) {
        this.preview = data;
        this.previewNotice = this.$t("ebay_exclusions.numbers_changed");
        this.endView = "preview";
        this.endDialog = true;
        return;
      }
      this.endView = "job_running_notice";
      this.endDialog = true;
    },
    reopenJob(item) {
      this.endExclusionId = item._id;
      this.endScope = item.scope;
      const jobId = this.runningJobs[item._id];
      this.endView = this.stalledJobs[jobId] ? "poll_error" : "progress";
      this.endDialog = true;
      if (!this.stalledJobs[jobId]) this.ensurePolling();
    },
    onEndDialogToggle() {
      // Closing the dialog does not stop polling. The row chip stays until a terminal status.
    },
    ensurePolling() {
      if (this.pollTimer) return;
      this.pollJobs();
      this.pollTimer = setInterval(() => this.pollJobs(), POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    pollJobs() {
      const ids = Object.values(this.runningJobs).filter((jobId) => jobId && !this.stalledJobs[jobId]);
      if (!ids.length) {
        this.stopPolling();
        return;
      }
      ids.forEach((jobId) => this.pollJob(jobId));
    },
    pollJob(jobId) {
      getRequest(`/ebay/exclusions/jobs/${jobId}`)
        .then((data) => {
          this.pollFailures = { ...this.pollFailures, [jobId]: 0 };
          const isVisible = this.runningJobs[this.endExclusionId] === jobId;
          if (isVisible) this.job = data;
          if (!TERMINAL_JOB.includes(data.status)) return;
          this.clearRunningJob(jobId);
          if (isVisible) this.finishVisibleJob(data);
        })
        .catch((err) => {
          if (this.handleAuthError(err)) {
            this.stopPolling();
            return;
          }
          const status = this.statusOf(err);
          if (status === 403 || status === 404) {
            this.clearRunningJob(jobId);
            this.handleEndHttpError(err);
            return;
          }
          const fails = (this.pollFailures[jobId] || 0) + 1;
          this.pollFailures = { ...this.pollFailures, [jobId]: fails };
          if (fails < POLL_FAIL_LIMIT) return;
          this.stalledJobs = { ...this.stalledJobs, [jobId]: true };
          if (this.runningJobs[this.endExclusionId] === jobId) {
            this.endView = "poll_error";
            this.endDialog = true;
          }
        });
    },
    clearRunningJob(jobId) {
      const next = { ...this.runningJobs };
      Object.keys(next).forEach((exclusionId) => {
        if (next[exclusionId] === jobId) delete next[exclusionId];
      });
      this.runningJobs = next;
      const stalled = { ...this.stalledJobs };
      delete stalled[jobId];
      this.stalledJobs = stalled;
      if (!Object.keys(this.runningJobs).length) this.stopPolling();
    },
    finishVisibleJob(data) {
      this.job = data;
      if (data.status === "completed") {
        this.showSnack(this.$t("ebay_exclusions.end_completed"), "success");
        this.endDialog = false;
        return;
      }
      if (data.status === "completed_with_errors") {
        this.endView = "errors";
        this.endDialog = true;
        return;
      }
      this.endView = "failed";
      this.endDialog = true;
    },
    retryPoll() {
      const jobId = this.runningJobs[this.endExclusionId];
      if (!jobId) return;
      const stalled = { ...this.stalledJobs };
      delete stalled[jobId];
      this.stalledJobs = stalled;
      this.pollFailures = { ...this.pollFailures, [jobId]: 0 };
      this.endView = "progress";
      this.ensurePolling();
    },
  },
};
</script>

<style scoped>
.institution-ebay-toolbar-title {
  font-weight: 500;
}
.institution-ebay-content {
  padding: 12px;
}
</style>
